import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Check, Circle } from 'lucide-react';
import { api, ApiError } from '../../api.ts';
import { formatEuros } from '../../format.ts';
import type { Invoice } from '../../components/InvoiceView.tsx';

interface Billing {
  profile: { companyName: string; siret: string; address: string; postalCode: string; city: string; vatRegime: 'FRANCHISE' | 'LIABLE' | null; vatNumber: string };
  missing: string[];
  mandate: { version: string; text: string; acceptedAt: string | null };
  platformCompanyConfigured: boolean;
  stripe: { connected: boolean; payoutsEnabled: boolean };
}

const PAYOUT_ERRORS: Record<string, string> = {
  BILLING_INCOMPLETE: 'Complétez d’abord vos informations de facturation.',
  MANDATE_REQUIRED: 'Acceptez d’abord le mandat de facturation.',
  PLATFORM_COMPANY_MISSING: 'Les coordonnées de facturation de Mon Extra ne sont pas encore configurées : contactez l’équipe.',
  NOTHING_DUE: 'Aucune commission payable pour le moment.',
  BELOW_MINIMUM: 'Le montant payable n’atteint pas encore le minimum de versement.',
  REFERRER_SUSPENDED: 'Votre compte est suspendu.',
};

const BILLING_ERRORS: Record<string, string> = {
  MISSING_FIELDS: 'Tous les champs de l’adresse et la raison sociale sont obligatoires.',
  INVALID_SIRET: 'Le SIRET doit comporter 14 chiffres.',
  INVALID_VAT_REGIME: 'Indiquez votre régime de TVA.',
  INVALID_VAT_NUMBER: 'Numéro de TVA intracommunautaire invalide (ex. FR12345678901).',
};

const INVOICE_BADGES: Record<Invoice['status'], { label: string; badge: string }> = {
  REQUESTED: { label: 'En attente de paiement', badge: 'badge-pending' },
  PAID: { label: 'Payée', badge: 'badge-available' },
  REJECTED: { label: 'Annulée', badge: 'badge-unavailable' },
};

function Step({ done, children }: { done: boolean; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: done ? 'var(--success)' : 'var(--text-secondary)', fontWeight: 600 }}>
      {done ? <Check size={15} /> : <Circle size={15} />} {children}
    </div>
  );
}

export function PayoutSection({ dueCents, minPayoutCents, onChanged }: { dueCents: number; minPayoutCents: number; onChanged: () => void }) {
  const [billing, setBilling] = useState<Billing | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [form, setForm] = useState<Billing['profile'] | null>(null);
  const [editing, setEditing] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get<Billing>('/referrer/billing').then((b) => {
      setBilling(b);
      setForm(b.profile);
      setEditing(b.missing.length > 0);
    });
    api.get<{ invoices: Invoice[] }>('/referrer/invoices').then((r) => setInvoices(r.invoices));
  }, []);

  useEffect(load, [load]);

  if (!billing || !form) return null;

  const set = (patch: Partial<Billing['profile']>) => setForm((f) => (f ? { ...f, ...patch } : f));
  const mandateAccepted = Boolean(billing.mandate.acceptedAt);
  const billingDone = billing.missing.length === 0;
  const belowMinimum = dueCents < minPayoutCents;
  const canRequest = billingDone && mandateAccepted && billing.platformCompanyConfigured && dueCents > 0 && !belowMinimum;

  async function saveBilling(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      const updated = await api.put<Billing>('/referrer/billing', form);
      setBilling(updated);
      setForm(updated.profile);
      setEditing(false);
      setMessage({ kind: 'ok', text: 'Informations de facturation enregistrées.' });
    } catch (err) {
      setMessage({ kind: 'error', text: err instanceof ApiError ? BILLING_ERRORS[err.code] ?? 'Enregistrement impossible.' : 'Enregistrement impossible.' });
    } finally {
      setBusy(false);
    }
  }

  async function toggleMandate() {
    setBusy(true);
    setMessage(null);
    try {
      if (mandateAccepted) {
        if (!window.confirm('Révoquer le mandat ? Vous ne pourrez plus demander de versement tant que vous ne l’aurez pas accepté à nouveau.')) return;
        await api.delete('/referrer/mandate');
      } else {
        await api.post('/referrer/mandate/accept');
      }
      load();
    } finally {
      setBusy(false);
    }
  }

  async function connectStripe() {
    setBusy(true);
    try {
      const res = await api.post<{ url: string }>('/referrer/stripe/onboarding');
      window.location.href = res.url;
    } catch {
      setMessage({ kind: 'error', text: 'Le service de paiement n’est pas disponible pour le moment.' });
      setBusy(false);
    }
  }

  async function requestPayout() {
    if (!window.confirm(`Générer ma facture de ${formatEuros(dueCents)} HT et demander le versement ? Elle vaudra acceptation de la facture.`)) return;
    setBusy(true);
    setMessage(null);
    try {
      const res = await api.post<{ invoice: Invoice }>('/referrer/payout-request');
      setMessage({ kind: 'ok', text: `Facture ${res.invoice.number} émise. Le versement sera effectué après vérification.` });
      load();
      onChanged();
    } catch (err) {
      setMessage({ kind: 'error', text: err instanceof ApiError ? PAYOUT_ERRORS[err.code] ?? 'Demande impossible.' : 'Demande impossible.' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 800 }}>Mes versements</div>

      {message && <div className={message.kind === 'ok' ? 'badge badge-available' : 'error-banner'} style={{ padding: '10px 14px' }}>{message.text}</div>}

      <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)', fontWeight: 700 }}>Commissions payables (HT)</div>
            <div style={{ fontSize: 24, fontWeight: 800 }}>{formatEuros(dueCents)}</div>
          </div>
          <button className="btn btn-primary" style={{ width: 'auto', padding: '11px 20px' }} disabled={!canRequest || busy} onClick={requestPayout}>
            Générer ma facture et demander le versement
          </button>
        </div>
        {belowMinimum && dueCents > 0 && (
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
            Minimum de versement : {formatEuros(minPayoutCents)}. Il vous manque {formatEuros(minPayoutCents - dueCents)}.
          </div>
        )}
        {!billing.platformCompanyConfigured && (
          <div className="badge badge-pending" style={{ padding: '10px 14px', fontSize: 12.5, lineHeight: 1.5 }}>
            Mon Extra n'a pas encore renseigné ses coordonnées de facturation : la demande de versement sera possible dès que ce sera fait.
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 4 }}>
          <Step done={billingDone}>Informations de facturation complètes</Step>
          <Step done={mandateAccepted}>Mandat de facturation accepté</Step>
          <Step done={billing.stripe.connected}>Compte de versement Stripe connecté (recommandé : virement automatique)</Step>
        </div>
        {!billing.stripe.connected && (
          <button className="btn btn-secondary btn-sm" style={{ alignSelf: 'flex-start' }} onClick={connectStripe} disabled={busy}>Connecter mon compte de versement</button>
        )}
      </div>

      <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontWeight: 800 }}>Mes informations de facturation</div>
          {!editing && <button className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}>Modifier</button>}
        </div>

        {editing ? (
          <form onSubmit={saveBilling} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
              <input className="input" required placeholder="Raison sociale" value={form.companyName} onChange={(e) => set({ companyName: e.target.value })} />
              <input className="input" required placeholder="SIRET (14 chiffres)" value={form.siret} onChange={(e) => set({ siret: e.target.value })} />
              <input className="input" required placeholder="Adresse" value={form.address} onChange={(e) => set({ address: e.target.value })} />
              <input className="input" required placeholder="Code postal" value={form.postalCode} onChange={(e) => set({ postalCode: e.target.value })} />
              <input className="input" required placeholder="Ville" value={form.city} onChange={(e) => set({ city: e.target.value })} />
              <select className="input" required value={form.vatRegime ?? ''} onChange={(e) => set({ vatRegime: (e.target.value || null) as Billing['profile']['vatRegime'] })}>
                <option value="">Régime de TVA…</option>
                <option value="FRANCHISE">Franchise en base de TVA (micro-entrepreneur)</option>
                <option value="LIABLE">Assujetti à la TVA</option>
              </select>
              {form.vatRegime === 'LIABLE' && (
                <input className="input" required placeholder="N° de TVA (FR…)" value={form.vatNumber} onChange={(e) => set({ vatNumber: e.target.value })} />
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary btn-sm" disabled={busy}>Enregistrer</button>
              {billingDone && <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setForm(billing.profile); setEditing(false); }}>Annuler</button>}
            </div>
          </form>
        ) : (
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            <strong style={{ color: 'var(--text)' }}>{billing.profile.companyName}</strong> · SIRET {billing.profile.siret}<br />
            {billing.profile.address}, {billing.profile.postalCode} {billing.profile.city}<br />
            {billing.profile.vatRegime === 'FRANCHISE' ? 'Franchise en base de TVA' : `Assujetti à la TVA · ${billing.profile.vatNumber}`}
          </div>
        )}
      </div>

      <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontWeight: 800 }}>Mandat de facturation (autofacturation)</div>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.6 }}>{billing.mandate.text}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button className={`btn btn-sm ${mandateAccepted ? 'btn-secondary' : 'btn-primary'}`} onClick={toggleMandate} disabled={busy}>
            {mandateAccepted ? 'Révoquer le mandat' : 'J’accepte le mandat'}
          </button>
          {billing.mandate.acceptedAt && (
            <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Accepté le {new Date(billing.mandate.acceptedAt).toLocaleDateString('fr-FR')}</span>
          )}
        </div>
      </div>

      <div>
        <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 10 }}>Mes factures</div>
        {invoices.length === 0 && <div className="empty-state">Aucune facture pour l'instant.</div>}
        {invoices.length > 0 && (
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>Numéro</th><th>Émise le</th><th>Total TTC</th><th>Statut</th><th /></tr></thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id}>
                    <td style={{ fontWeight: 700 }}>{inv.number}</td>
                    <td>{new Date(inv.issuedAt).toLocaleDateString('fr-FR')}</td>
                    <td>{formatEuros(inv.totalCents)}</td>
                    <td><span className={`badge ${INVOICE_BADGES[inv.status].badge}`}>{INVOICE_BADGES[inv.status].label}</span></td>
                    <td style={{ textAlign: 'right' }}><Link to={`/apporteur/factures/${inv.id}`} className="btn btn-secondary btn-sm" style={{ display: 'inline-flex' }}>Voir</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
